import React, { useState, useEffect } from 'react';

/**
 * TicTacToe component for the CheckinComplete page
 * Interactive Tic-Tac-Toe game with game logic
 */
const TicTacToe = () => {
    // Game state
    const [board, setBoard] = useState(Array(9).fill(null));
    const [isXNext, setIsXNext] = useState(true);
    const [gameStatus, setGameStatus] = useState('New');
    const [statusColor, setStatusColor] = useState({ bg: '#e8f5e9', color: '#4caf50' });

    // Calculate winner
    const calculateWinner = (squares) => {
        const lines = [
            [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
            [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
            [0, 4, 8], [2, 4, 6]             // diagonals
        ];

        for (let i = 0; i < lines.length; i++) {
            const [a, b, c] = lines[i];
            if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
                return squares[a];
            }
        }
        return null;
    };

    // Check if board is full (draw)
    const isBoardFull = (squares) => {
        return squares.every(square => square !== null);
    };

    // Handle click on a square
    const handleClick = (index) => {
        // Don't allow moves if game is over or square is already filled
        if (calculateWinner(board) || board[index]) {
            return;
        }

        // Create a copy of the board
        const newBoard = [...board];
        
        // Make the move
        newBoard[index] = isXNext ? 'X' : 'O';
        
        // Update the board state
        setBoard(newBoard);
        
        // Switch turns
        setIsXNext(!isXNext);
        
        // Update game status to 'In Progress' after first move
        if (gameStatus === 'New') {
            setGameStatus('In Progress');
            setStatusColor({ bg: '#fff3e0', color: '#ff9800' });
        }
    };

    // Reset the game
    const resetGame = () => {
        setBoard(Array(9).fill(null));
        setIsXNext(true);
        setGameStatus('New');
        setStatusColor({ bg: '#e8f5e9', color: '#4caf50' });
    };

    // Update game status when board changes
    useEffect(() => {
        const winner = calculateWinner(board);
        
        if (winner) {
            setGameStatus(`${winner} Wins!`);
            setStatusColor({ bg: '#e8f5e9', color: '#4caf50' });
        } else if (isBoardFull(board)) {
            setGameStatus('Draw');
            setStatusColor({ bg: '#e3f2fd', color: '#2196f3' });
        }
    }, [board]);

    // Render a square
    const renderSquare = (index) => {
        return (
            <div className="col-4" key={index}>
                <div 
                    className="p-2 border text-center" 
                    style={{ 
                        cursor: board[index] || calculateWinner(board) ? 'default' : 'pointer',
                        backgroundColor: board[index] ? (board[index] === 'X' ? '#f8f9fe' : '#fff8e1') : 'white',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '1.2rem'
                    }}
                    onClick={() => handleClick(index)}
                >
                    {board[index]}
                </div>
            </div>
        );
    };

    // Convert 1D array to 2D for rendering
    const renderBoard = () => {
        return [
            board.slice(0, 3),  // First row
            board.slice(3, 6),  // Second row
            board.slice(6, 9)   // Third row
        ];
    };

    return (
        <div className="card mb-3" style={{ borderRadius: '8px', border: '1px solid #eee' }}>
            <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Tic-Tac-Toe</h6>
                    <span className="badge" style={{ 
                        backgroundColor: statusColor.bg, 
                        color: statusColor.color, 
                        fontSize: '0.7rem' 
                    }}>
                        {gameStatus}
                    </span>
                </div>
                <div className="tic-tac-toe-grid">
                    {renderBoard().map((row, rowIndex) => (
                        <div className={`row g-2 ${rowIndex < 2 ? 'mb-2' : ''}`} key={rowIndex}>
                            {row.map((_, colIndex) => renderSquare(rowIndex * 3 + colIndex))}
                        </div>
                    ))}
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                        {!calculateWinner(board) && !isBoardFull(board) && 
                            `Next player: ${isXNext ? 'X' : 'O'}`
                        }
                    </div>
                    <button 
                        className="btn btn-sm" 
                        onClick={resetGame}
                        style={{ 
                            backgroundColor: '#5f63f2', 
                            color: 'white',
                            fontSize: '0.7rem',
                            padding: '0.25rem 0.5rem'
                        }}
                    >
                        New Game
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TicTacToe;
